import { useAppSelector } from "../../../../hooks";
import { ReuseModal } from "../../ReuseModal";
import { Stack, Typography } from "@mui/material";
import { AccountPlanStatus, ModalSize } from "../../../../types/enums";
import { useGetAccountsPlanListQuery } from "../../../../api";
import { useParams } from "react-router-dom";
import { useMemo, useState } from "react";
import { ModalListWithSelect } from "../../ModalListWithSelect";

const formattedDate = (dateStr: string) => {
  const date = new Date(dateStr);
  const today = new Date();

  const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const d1 = startOfDay(date);
  const d2 = startOfDay(today);

  const diffMs = d2.getTime() - d1.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  const isToday = diffDays === 0;

  if (isToday) return "Today";

  if (diffDays >= 1 && diffDays <= 6) {
    return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  }

  const sameYear = date.getFullYear() === today.getFullYear();

  if (sameYear) {
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
    });
  }

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const PER_PAGE = 25;
const START_PAGE = 0;

export const AccountPlanModal = ({
  onClose,
  onSubmit,
  isLoading,
  title,
  description,
  submitBtnText,
  status_filter,
}: {
  onClose: () => void;
  onSubmit: (selected: string[], includeAll: boolean) => void;
  isLoading: boolean;
  title: string;
  description: string;
  submitBtnText: string;
  status_filter: AccountPlanStatus;
}) => {
  const { campaign_id } = useParams();
  const campaignId = useAppSelector((state) => state.company.company);

  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(START_PAGE);
  const [isSelectedAll, setIsSelectedAll] = useState<boolean>(true);

  const {
    data: companies,
    isLoading: isCompaniesLoading,
    isFetching,
  } = useGetAccountsPlanListQuery(
    {
      campaign_id: campaign_id || campaignId,
      page,
      per_page: PER_PAGE,
      search: search || undefined,
      status_filter,
    },
    { refetchOnMountOrArgChange: true },
  );

  const prepareAccounts = useMemo(
    () =>
      companies?.items.map(({ account_id, account_name, updated_at }) => ({
        id: account_id,
        name: account_name,
        missed: 0,
        value: (
          <Typography
            variant="body1"
            fontWeight={500}
            color="custom.textDarkBlue"
          >
            {formattedDate(updated_at)}
          </Typography>
        ),
      })),
    [companies?.items],
  );

  const submitHandler = () => {
    onSubmit(selected, isSelectedAll);
  };

  const setSearchHandler = (value: string) => {
    setSearch(value);
    setPage(START_PAGE);
  };

  return (
    <ReuseModal
      isOpen={true}
      isLoading={isCompaniesLoading || isLoading}
      onClose={onClose}
      title={title}
      onSubmit={submitHandler}
      maxWidth={ModalSize.mid}
      submitBtnColor="#1C3C6C"
      submitBtnText={submitBtnText}
      isDisabled={selected.length === 0 || isCompaniesLoading || isLoading}
      isDisabledCancelBtn={isLoading}
      cancelBtnText={"Back"}
    >
      <Stack gap="16px">
        <Stack>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            fontWeight={500}
          >
            {description}
          </Typography>
          {companies && (
            <ModalListWithSelect
              page={page}
              loadMore={() => setPage((prev) => prev + 1)}
              isLoading={isFetching}
              canLoadMore={companies.total_pages > page + 1}
              headerRightTitle={"Last Updated"}
              itemsList={prepareAccounts || []}
              withSelectAll
              withHeader={true}
              setIsSelectedAll={setIsSelectedAll}
              setSelectedHandler={setSelected}
              setSearchHandler={setSearchHandler}
              startPage={START_PAGE}
            />
          )}
        </Stack>
      </Stack>
    </ReuseModal>
  );
};
